/* FTE Report — Details tab: "Forecasted New Hire Report". Three separate tables (Employees /
   IWO Employees / Subcontractors), each with its own header and pagination. Owns the data fetch
   and the month show/hide state (MonthColumnPicker, same component Summary.js uses);
   ./DetailsTable.js and ./DetailsSectionTable.js are pure rendering. */
import React, { useEffect, useMemo, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getFteDetailsAPI } from "../../../../api/reports-api";
import { unwrapFteResponse, mapFteDetails } from "./fteReportUtils";
import DetailsTable from "./DetailsTable";

const HIDDEN_MONTHS_KEY = "fteReportDetailsHiddenMonths";

export default function Details({ filters, dateRange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hiddenMonths, setHiddenMonths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HIDDEN_MONTHS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const apiKey = useMemo(() => JSON.stringify({ filters, dateRange }), [filters, dateRange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFteDetailsAPI(filters, dateRange)
      .then((res) => {
        if (cancelled) return;
        const body = unwrapFteResponse(res);
        setData(body ? mapFteDetails(body) : null);
        if (!body) setError(res?.message || "Failed to load data");
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError("Failed to load data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const toggleMonth = (key) => {
    setHiddenMonths((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem(HIDDEN_MONTHS_KEY, JSON.stringify(next));
      return next;
    });
  };
  const resetMonths = () => {
    setHiddenMonths([]);
    localStorage.setItem(HIDDEN_MONTHS_KEY, "[]");
  };

  const allMonths = data?.monthKeys || [];
  const visibleMonths = allMonths.filter((k) => !hiddenMonths.includes(k));

  const sections = data?.sections || [];
  const totalCount = useMemo(
    () => sections.reduce(
      (n, s) => n + s.rows.reduce((m, r) => m + Object.values(r.months || {}).filter(Boolean).length, 0),
      0,
    ),
    [sections],
  );

  if (loading) return <Loading />;
  if (error) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: "#D64550", fontSize: 13 }}>
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div style={{ paddingBottom: 24 }}>
      <DetailsTable
        sections={sections}
        totalCount={totalCount}
        allMonths={allMonths}
        actualMonths={data.actualMonths}
        visibleMonths={visibleMonths}
        hiddenMonths={hiddenMonths}
        onToggleMonth={toggleMonth}
        onResetMonths={resetMonths}
      />
    </div>
  );
}
